import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
} from '@syncfusion/ej2-react-grids';

import {
  useGetRefundWorklistQuery,
  RefundWorklistView,
  RefundWorklistType,
  REFUND_WORKLIST_TYPES,
} from '../../../../../services/RefundService';
import { useWorklistSearch, useWorklistNavigation } from './hooks';
import { SearchBar } from './components';
import {
  PatientTemplate,
  AccountTemplate,
  RefundIdTemplate,
  DateTemplate,
  AmountTemplate,
  RefundStatusChipTemplate,
  PriorityChipTemplate,
  SageStatusTemplate,
} from './templates';

export interface RefundWorklistGridProps {
  testId?: string;
}

// Search fields for filtering
const SEARCH_FIELDS: (keyof RefundWorklistView)[] = [
  'lastName',
  'firstName',
  'accountNumber',
  'refundId',
  'caseId',
  'refundStatus',
  'priority',
  'sageStatus',
];

// Grid settings
const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };
const sortSettings = { allowUnsort: false };

// Grid styling
const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

/**
 * RefundWorklistGrid - Displays refund worklist items
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation
 * - Reusable search bar component
 */
export const RefundWorklistGrid: React.FC<RefundWorklistGridProps> = ({ testId }) => {
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const typeParam = searchParams.get('type') || '';

  // Validate type parameter
  const type: RefundWorklistType = REFUND_WORKLIST_TYPES.includes(typeParam as RefundWorklistType)
    ? (typeParam as RefundWorklistType)
    : 'Started';

  // Fetch worklist data
  const {
    data: worklistItems = [],
    isLoading,
    error,
  } = useGetRefundWorklistQuery(
    { type, center, company },
    {
      skip: !type || !center || !company,
    }
  );

  // Use custom hooks
  const { searchTerm, filteredItems, handleSearchChange, handleClearSearch } = useWorklistSearch(
    worklistItems,
    SEARCH_FIELDS
  );
  const { handleRowSelected } = useWorklistNavigation(company, center);

  // Create templates
  const patientTemplate = useCallback(
    (rowData: RefundWorklistView) => (
      <PatientTemplate firstName={rowData.firstName} lastName={rowData.lastName} />
    ),
    []
  );

  const accountTemplate = useCallback(
    (rowData: RefundWorklistView) => <AccountTemplate accountNumber={rowData.accountNumber} />,
    []
  );

  const refundIdTemplate = useCallback(
    (rowData: RefundWorklistView) => <RefundIdTemplate refundId={rowData.refundId} />,
    []
  );

  const serviceDateTemplate = useCallback(
    (rowData: RefundWorklistView) => <DateTemplate date={rowData.serviceDate} />,
    []
  );

  const refundStartDateTemplate = useCallback(
    (rowData: RefundWorklistView) => <DateTemplate date={rowData.refundStartDate} />,
    []
  );

  const amountTemplate = useCallback(
    (rowData: RefundWorklistView) => <AmountTemplate amount={rowData.amount} />,
    []
  );

  const refundStatusTemplate = useCallback(
    (rowData: RefundWorklistView) => <RefundStatusChipTemplate refundStatus={rowData.refundStatus} />,
    []
  );

  const priorityTemplate = useCallback(
    (rowData: RefundWorklistView) => <PriorityChipTemplate priority={rowData.priority} />,
    []
  );

  const sageStatusTemplate = useCallback(
    (rowData: RefundWorklistView) => <SageStatusTemplate sageStatus={rowData.sageStatus} />,
    []
  );

  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading refund worklist...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading refund worklist. Please try again.</Typography>
      </Box>
    );
  }

  // Handle empty results
  if (worklistItems.length === 0) {
    return (
      <Box>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClear={handleClearSearch}
        />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No refunds found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No refunds available for {type} in {center}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredItems.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClear={handleClearSearch}
        />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No refunds match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClear={handleClearSearch}
      />

      {/* Results Summary */}
      {searchTerm.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredItems.length} of {worklistItems.length} refunds
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredItems}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="patient"
              headerText="Patient"
              width="150"
              textAlign="Left"
              template={patientTemplate}
            />
            <ColumnDirective
              field="accountNumber"
              headerText="Account"
              width="120"
              textAlign="Left"
              template={accountTemplate}
            />
            <ColumnDirective
              field="refundId"
              headerText="Refund ID"
              width="100"
              textAlign="Left"
              template={refundIdTemplate}
            />
            <ColumnDirective
              field="amount"
              headerText="Amount"
              width="100"
              textAlign="Right"
              template={amountTemplate}
            />
            <ColumnDirective
              field="refundStatus"
              headerText="Status"
              width="120"
              textAlign="Center"
              template={refundStatusTemplate}
            />
            <ColumnDirective
              field="priority"
              headerText="Priority"
              width="100"
              textAlign="Center"
              template={priorityTemplate}
            />
            <ColumnDirective
              field="serviceDate"
              headerText="Service Date"
              width="120"
              textAlign="Center"
              template={serviceDateTemplate}
            />
            <ColumnDirective
              field="refundStartDate"
              headerText="Start Date"
              width="120"
              textAlign="Center"
              template={refundStartDateTemplate}
            />
            <ColumnDirective
              field="sageStatus"
              headerText="Sage Status"
              width="110"
              textAlign="Left"
              template={sageStatusTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default RefundWorklistGrid;
